<template>
    <form-wrapper @submit="handleFormSubmission(data)">
        <form-row>
            <form-cell>
                <select-field
                    :label="$t('forms.referrals.search.fields.patient_insurance.label')"
                    :placeholder="$t('forms.referrals.search.fields.patient_insurance.placeholder')"
                    :isRequired="true"
                    :groupOptions="true"
                    :expandOptionsRight="true"
                    :options="insurancePlans"
                    :isSearchable="true"
                    :isDisabled="isInsuranceLocked"
                    :hasAutofocus="true"
                    :formData="data"
                    icon="insurance"
                    valuePath="insurancePlanId"
                    @change="change($event)"
                ></select-field>
            </form-cell>
        </form-row>

        <template v-if="data.practiceType === 'physician'">
            <form-row>
                <form-cell>
                    <select-field
                        :label="$t('forms.referrals.search.fields.speciality.label')"
                        :placeholder="$t('forms.referrals.search.fields.speciality.placeholder')"
                        :isRequired="true"
                        :hasAutofocus="true"
                        :options="specialities"
                        :formData="data"
                        valuePath="specialityId"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <select-field
                        :label="$t('forms.referrals.search.fields.subspeciality.label')"
                        :placeholder="$t('forms.referrals.search.fields.subspeciality.placeholder')"
                        :isDisabled="hasNoSubSpecialities(data)"
                        :options="subSpecialities(data)"
                        :formData="data"
                        valuePath="subSpecialityId"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <tabs-field
                        :label="$t('forms.referrals.search.fields.search_by_time_window.label')"
                        :options="searchTimeSlotOptions"
                        :formData="data"
                        valuePath="searchByTimeWindow"
                        :size="isMobileLarge ? 'extra-small' : 'small'"
                        @change="change($event)"
                    ></tabs-field>
                </form-cell>
            </form-row>
        </template>

        <template v-else-if="data.practiceType === PracticeTypeEnum.ANCILLARY">
            <form-row>
                <form-cell>
                    <common-search-autocomplete
                        ref="searchAC"
                        :label="$t('forms.referrals.search.fields.tests.label')"
                        :placeholder="$t('forms.referrals.search.fields.tests.placeholder')"
                        :icon="'ancillary-tests'"
                        :items="testsList"
                        :selectedItems="selectedTests"
                        :isSearching="isSearchingTests"
                        @item-selected="itemSelected($event)"
                        @input-change="inputChanged($event)"
                        @remove-item="removeItem($event)"
                        @no-results="clearTestList()"
                    ></common-search-autocomplete>
                </form-cell>
            </form-row>
            <common-badges-list-remove
                ref="badgeList"
                :listElements="selectedTests"
                @remove-item="removeItem($event)"
            ></common-badges-list-remove>
        </template>

        <form-row>
            <form-cell>
                <radio-field
                    :label="$t('forms.referrals.search.fields.telehealth.label')"
                    :isRequired="true"
                    :formData="data"
                    :options="telehealthOptions"
                    valuePath="telehealth"
                    @change="change($event)"
                ></radio-field>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell>
                <radio-field
                    :label="$t('forms.referrals.search.fields.search_by.label')"
                    :isRequired="true"
                    :formData="data"
                    :options="searchByOptions"
                    valuePath="searchBy"
                    @change="change($event)"
                ></radio-field>
            </form-cell>
        </form-row>

        <template v-if="data.searchBy === 'zipcode'">
            <form-row>
                <form-cell width="half">
                    <text-field
                        :placeholder="$t('forms.referrals.search.fields.patient_zipcode.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        valuePath="zip"
                        :maxLength="5"
                        :minlength="5"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>
        </template>

        <template v-else-if="data.searchBy === 'patient_address'">
            <form-row>
                <form-cell width="full">
                    <text-field
                        :label="$t('forms.referrals.search.fields.patient_address.label')"
                        :placeholder="$t('forms.referrals.search.fields.patient_address.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        valuePath="address"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell width="full">
                    <text-field
                        :label="$t('forms.referrals.search.fields.patient_city.label')"
                        :placeholder="$t('forms.referrals.search.fields.patient_city.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        valuePath="city"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell width="half">
                    <select-field
                        :label="$t('forms.referrals.search.fields.patient_state.label')"
                        :placeholder="$t('forms.referrals.search.fields.patient_state.placeholder')"
                        :isRequired="true"
                        :options="states"
                        :formData="data"
                        valuePath="state"
                        @change="change($event)"
                    ></select-field>
                </form-cell>

                <form-cell width="half">
                    <text-field
                        :label="$t('forms.referrals.search.fields.patient_zipcode.label')"
                        :placeholder="$t('forms.referrals.search.fields.patient_zipcode.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        valuePath="zip"
                        :maxLength="5"
                        :minlength="5"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>
        </template>

        <template #footer>
            <common-buttons>
                <common-button
                    :isProcessing="isSending"
                    :label="$t('forms.referrals.search.buttons.send')"
                    type="primary"
                    size="large"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import debounce from 'lodash/debounce'
    import FormMixin from 'mixins/FormMixin'
    import FormSpecialitiesMixin from 'mixins/FormSpecialitiesMixin'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import CommonSearchAutocomplete from '../page-elements/search-autocomplete/CommonSearchAutocomplete.vue'
    import FacilityServicesAPI from 'api/FacilityServicesAPI'
    import TimeWindowSearchCriteriaEnum from '@/enums/TimeWindowSearchCriteriaEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import config from '../../config'
    import TelehealthTypeEnum from '@/enums/TelehealthTypeEnum'

    export default {
        mixins: [FormMixin, FormSpecialitiesMixin, ScreenSizeDetectionMixin],

        components: { CommonSearchAutocomplete },

        props: {
            practiceType: { type: String, default: 'physician' },
            states: { type: Array, required: true },
            insurancePlans: { type: Array, required: true },
            facilityServices: { type: Array, required: true },
            patientDetails: { type: Object, required: false, default: undefined },
        },

        data() {
            return {
                data: {
                    userId: undefined,
                    practiceType: 'physician',
                    searchBy: 'zipcode',
                    searchByTimeWindow: 'asap',
                    insurancePlanId: this.patientDetails?.mcard?.primaryInsuranceTypeId ?? null,
                    zip: this.patientDetails?.zip ?? null,
                    address: this.patientDetails?.address ?? null,
                    city: this.patientDetails?.city ?? null,
                    state: this.patientDetails?.state ?? null,
                    telehealth: TelehealthTypeEnum.IN_PERSON_ONLY,
                },
                isInsuranceLocked: false,
                testsSearchRequestId: 0,
                pendingTestsSearchCount: 0,
                PracticeTypeEnum: PracticeTypeEnum,
                testsList: [],
                selectedTests: [],
            }
        },

        computed: {
            isServiceSelected() {
                return this.data?.facilityServiceIds?.length
            },

            isSearchingTests() {
                return this.pendingTestsSearchCount > 0
            },

            searchByOptions() {
                return ['zipcode', 'patient_address'].map((value) => ({
                    label: this.$t(`forms.referrals.search.fields.search_by.options.${value}`),
                    value,
                }))
            },

            searchTimeSlotOptions() {
                return TimeWindowSearchCriteriaEnum
            },

            telehealthOptions() {
                return Object.keys(TelehealthTypeEnum).map((value) => ({
                    label: this.$t(`forms.referrals.search.fields.telehealth.options.${value}`),
                    value: TelehealthTypeEnum[value],
                }))
            },
        },

        methods: {
            handleFormSubmission(data) {
                if (this.$auth.isUserPracticeDemo()) {
                    this.emitEvent({ name: 'open-paywall-modal' })
                    return false
                }

                // Clean unwanted data based on searchBy
                if (data.searchBy === 'zipcode') {
                    delete data.address
                    delete data.city
                    delete data.state
                }

                data.testIds = this.selectedTests.map((item) => item.id)
                data.selectedTests = this.selectedTests
                data.serviceId = this.selectedTests[0]?.serviceId
                data.orderByPhysicianGroupId = this.$auth.getUser().physicianGroupId

                this.isSending = true
                this.emitSent(data)
            },

            itemSelected(item) {
                this.selectedTests.push(item)
                this.testsList = []
                this.$refs.searchAC.searchTerm = ''
                this.$refs.badgeList.calculateMore()
            },

            inputChanged(event) {
                if (event.searchTerm.length < 3) {
                    this.testsSearchRequestId += 1
                    this.testsList = []
                    return
                }

                this.searchTests(event.searchTerm)
            },

            searchTests: debounce(function (searchTerm) {
                const requestId = (this.testsSearchRequestId += 1)
                this.pendingTestsSearchCount += 1

                FacilityServicesAPI.getFacilityServiceTestSearch({ text: searchTerm })
                    .then((response) => {
                        if (requestId !== this.testsSearchRequestId) return

                        if (response.length > 0) {
                            this.testsList = response
                        } else {
                            this.testsList = []
                        }
                    })
                    .finally(() => {
                        this.pendingTestsSearchCount -= 1
                    })
            }, config.DEBOUNCE.DEFAULT),

            removeItem(event) {
                const itemIndex = this.selectedTests.map((item) => item.id).indexOf(event.id)
                this.selectedTests.splice(itemIndex, 1)
                this.$refs.badgeList.calculateMore()
            },

            clearTestList() {
                this.testsList = []
            },

            resetComponentData() {
                this.data = {
                    userId: this.$auth.getActiveMember() ? this.$auth.getActiveMember().id : undefined,
                    practiceType: this.$route.query.practiceType,
                    searchBy: 'zipcode',
                }
                this.isInsuranceLocked = false
                this.testsList = []
                this.selectedTests = []
            },

            getButtonType(value) {
                if (this.data.searchByTimeWindow === value) {
                    return 'primary'
                }
                return 'secondary'
            },

            changeSearchByTimeWindow(value) {
                this.data.searchByTimeWindow = value
                this.change(value)
            },
        },

        watch: {
            $route() {
                this.data.practiceType = this.$route.query.practiceType
            },
        },

        mounted() {
            if (this.$auth.getActiveMember() && this.$auth.getActiveMember().id) {
                this.userId = this.$auth.getActiveMember().id
            } else if (this.$auth.getUser() && this.$auth.getUser().id) {
                this.userId = this.$auth.getUser().id
            }

            if (this.practiceType) {
                this.data.practiceType = this.practiceType
            }
            if (this.$route.params.prefillData) {
                this.isInsuranceLocked = !!this.$route.params.prefillData.insurancePlanId
                this.prefillData(this.$route.params.prefillData)
                return
            }

            const prefillDataObject = {}
            Object.keys(this.$route.query).forEach((queryParamName) => {
                const value = this.$route.query[queryParamName]
                if (!isNaN(value)) {
                    prefillDataObject[queryParamName] = Number(value)
                } else {
                    prefillDataObject[queryParamName] = value
                }
            })

            if (Object.keys(prefillDataObject).length > 1) {
                // > 1 because limit is always present
                this.prefillData(prefillDataObject)
            }
        },
    }
</script>

<style lang="scss">
    .time-window {
        display: flex;
        align-items: center;
        justify-content: center;
    }
</style>
